import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Ban, MoreVertical, Pencil, RotateCcw, Trash2, Undo2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { costRfqStatusVariant } from '@/lib/status-variants'
import { RFQ_STATUS_KEY } from '../lib/labels'
import { allQuoted, type RfqAffordances } from '../lib/rfq'
import type { CostRfq } from '../types'

interface Props {
  rfq: CostRfq
  af: RfqAffordances
  onBack: () => void
  onEdit: () => void
  onCancel: () => void
  onReopen: () => void
  onRevoke: () => void
  onDelete: () => void
}

/** Part D spec §8: number, title, status and the RFQ's actions. */
const RfqHeader: React.FC<Props> = ({ rfq, af, onBack, onEdit, onCancel, onReopen, onRevoke, onDelete }) => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-wrap items-start gap-3">
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onBack} aria-label={t('CostControl_rfq_back')}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-muted-foreground">{t('CostControl_rfq_number', { number: rfq.number })}</span>
          <h1 className="truncate text-xl font-semibold">{rfq.title}</h1>
          <Badge variant={costRfqStatusVariant(rfq.status)}>{t(RFQ_STATUS_KEY[rfq.status])}</Badge>
          {allQuoted(rfq) && <Badge variant="warning">{t('CostControl_rfq_all_quoted')}</Badge>}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {af.revoke && (
          <Button variant="outline" size="sm" onClick={onRevoke}>
            <Undo2 className="h-4 w-4" />
            {t('CostControl_rfq_revoke')}
          </Button>
        )}
        {af.reopen && (
          <Button variant="outline" size="sm" onClick={onReopen}>
            <RotateCcw className="h-4 w-4" />
            {t('CostControl_rfq_reopen')}
          </Button>
        )}
        {af.edit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
            {t('Common_edit')}
          </Button>
        )}
        {(af.cancel || af.remove) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('CostControl_rfq_more')}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {af.cancel && (
                <DropdownMenuItem onSelect={onCancel}>
                  <Ban className="h-4 w-4" />
                  {t('CostControl_rfq_cancel')}
                </DropdownMenuItem>
              )}
              {af.remove && (
                <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={onDelete}>
                  <Trash2 className="h-4 w-4" />
                  {t('Common_delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}
export default RfqHeader
